import { sectionLabels } from "../section-labels";
import { colorFields, collectionLayoutField } from "./_shared";
/**
 * Collections section — horizontal scrollable strip of category tiles.
 * Renders only when the store has 2+ categories. Settings are minimal because
 * the content is data-driven (categories) rather than authored copy.
 */
export const collectionsSchema = {
    type: "collections",
    name: sectionLabels["collections"],
    description: {
        ar: "شريط أفقي بكروت الفئات. بيظهر تلقائيًا لو عندك فئتين أو أكتر.",
        en: "Horizontal strip of category tiles. Appears when you have 2+ categories.",
    },
    // RAISED 1 → 3 (2026-07-20, `nature` family). The cap existed to stop a
    // seller stacking redundant copies of one category grid, and with a single
    // layout that was the only thing a second instance could be. It is no longer
    // true: `nature` ships THREE distinct category presentations on one page —
    // a heading rail near the top, a marquee ticker mid-page, and a promo-card
    // carousel below it — and they do different jobs. Capping at 1 would have
    // made that family unreproducible, which is exactly the signal this
    // workstream exists to catch. No published store moves: raising a maximum
    // cannot invalidate an existing template.
    maxPerTemplate: 3,
    availableOnTemplates: ["home"],
    settings: [
        collectionLayoutField(),
        {
            id: "title",
            type: "text",
            i18n: true,
            label: { ar: "عنوان القسم", en: "Section title" },
            validate: { max: 60 },
            // Matches `BuyerStoreHomepage`'s own fallback verbatim, and has to: the
            // editor shows this default as the input's PLACEHOLDER — "leave this
            // empty and here is what your store prints" — so the two saying different
            // things would make the hint a lie. Aligned to the RENDERER's wording
            // rather than the other way round, because changing the renderer would
            // re-word the heading on every published store that never set one.
            default: { ar: "تسوق حسب المجموعة", en: "Shop by Collection" },
        },
        // The kicker above the title. `BuyerCollectionGrid` has always RENDERED an
        // eyebrow (the `caption-tile` variant even prints it on every tile), but
        // the schema never declared the field — so it was unreachable from the
        // inspector and only settable by hand-editing the JSON. Declared here
        // because avyro's categories block leads with "best seller collections"
        // above "Shop by Category", and that kicker is part of the layout's
        // identity, not decoration.
        //
        // Note `description` is in the same position (rendered by the `rail`
        // variant, undeclared). Left alone: no family built so far needs it, and
        // guessing at its copy limits would be inventing a control.
        {
            id: "eyebrow",
            type: "text",
            i18n: true,
            label: { ar: "نص صغير فوق العنوان (اختياري)", en: "Eyebrow (optional)" },
            helpText: {
                ar: 'نص صغير فوق عنوان القسم، مثل "أفضل المجموعات".',
                en: 'A small label above the section title, e.g. "Best seller collections".',
            },
            validate: { max: 40 },
        },
        {
            /**
             * Whether the section prints its own heading.
             *
             * Added 2026-07-20 for the `hype` family, whose `ab-figma-categories`
             * rail ships its `.ab-categories-title` element EMPTY — the tiles' own
             * kicker + label are the whole of its copy. There was no way to express
             * that: `title` falls back to a built-in "Shop by Collection" whenever
             * it resolves empty, so a theme could not turn the heading off.
             *
             * Deliberately the SAME field id and semantics `category-sections`
             * already uses, rather than a new vocabulary for one concept. Defaults
             * to `true`, which is what every existing store already renders, so
             * applying this field to a published theme cannot move it.
             */
            id: "showSectionTitle",
            type: "checkbox",
            label: { ar: "إظهار عنوان القسم", en: "Show section title" },
            helpText: {
                ar: "اقفلها لو الكروت نفسها كفاية من غير عنوان فوقها.",
                en: "Turn off when the tiles speak for themselves and need no heading.",
            },
            default: true,
        },
        {
            /**
             * Categories this section LEAVES OUT. Section-local and reversible: the
             * bin in the editor's collection list adds an id here, and the row
             * reappears under «رجّعها» ready to be tapped back in.
             *
             * An EXCLUSION list rather than the inclusion list a `categoryPicker`
             * normally is, for one reason: the section has always rendered every
             * top-level category implicitly, so an inclusion list would read as
             * empty on every published store and blank the section. Empty here means
             * "leave nothing out", which is what every store already does.
             *
             * `authorOnly` because it HAS no control of its own — the collection list
             * is its control, the same way the list is what edits `sortOrder` and
             * `isActive`. It is a `categoryPicker` for its shape (an id array that
             * `merge` carries as seller content, so removals survive a theme apply)
             * and for the validator it already has.
             *
             * Ids that no longer exist are inert: the buyer filters by membership, so
             * a deleted category simply stops matching.
             */
            id: "hiddenCategoryIds",
            type: "categoryPicker",
            authorOnly: true,
            // Never rebound by a theme/catalog apply — see `sellerOwned` in types.ts.
            sellerOwned: true,
            label: { ar: "فئات متشالة من القسم", en: "Categories removed from this section" },
            default: [],
        },
        {
            id: "count",
            type: "range",
            /**
             * HIDDEN FROM THE EDITOR (2026-09-16), not deleted. Which collections
             * appear is chosen in the section's own list now — add, hide, reorder —
             * and a second control that silently truncates that list to the first N
             * was the same decision asked twice, with the quieter answer winning.
             *
             * `authorOnly` hides the control and keeps the value, so no published
             * store re-renders today. NOTE that the cap therefore still applies on
             * the storefront for the variants whose `showIf` lists them below: a
             * store with `count: 1` shows one tile no matter what its list says.
             * Removing the cap is a buyer-renderer change and a visible one for
             * every store that ever set this, so it is a separate decision.
             */
            authorOnly: true,
            label: { ar: "عدد الفئات المعروضة", en: "Categories shown" },
            helpText: {
                ar: "عدد كروت الفئات اللي هتظهر. 1 = صف واحد ممتد، 2 = نصين، 3 = تلاتة، 4 = شبكة 2×2.",
                en: "How many category tiles to show. 1 = full width, 2 = halves, 3 = thirds, 4 = 2×2 grid.",
            },
            min: 1,
            max: 4,
            step: 1,
            default: 3,
            // A6: the compact strip + chips variants render the FULL category list, so
            // the 1–4 cap does nothing there. `bento` is the same — its tall/short
            // rhythm only reads as a mosaic with the whole list, and so is `rail`
            // (a carousel that scrolls the full set).
            showIf: {
                variant: [
                    "standard",
                    "overlay-pill",
                    "slider",
                    "list",
                    "masonry",
                    "featured",
                ],
            },
        },
        {
            /**
             * How the BENTO mosaic collapses on small screens.
             *
             * A SETTING rather than a second variant, and the evidence is that their
             * own two `ab-categories-section` families disagree while sharing every
             * other value. MEASURED 2026-07-20 at 390px:
             *
             *   elite-furniture — `display:grid`, one 354px column, section 1618px
             *   sharp-bags      — `display:flex`, snap scroller, section 331px
             *
             * and at 1440 both are the identical 54.5% / 45.5% mosaic with the same
             * 20px gap, the same `rgba(0,0,0,.2)` dim and the same 28px/w500/ls .56px
             * uppercase white tile label. Two variants for one layout that differs
             * only in how it folds is the redundancy that got 7 product cards
             * deleted; this is the same call the plan doc makes for logo-on/off and
             * newsletter-on/off ("seller toggles, not variants").
             *
             * Defaults to `stack`, which is what every existing store already
             * renders — applying this field to a published theme cannot move it.
             */
            id: "mobileLayout",
            type: "select",
            label: { ar: "العرض على الموبايل", en: "On small screens" },
            helpText: {
                ar: "تحت 1024 بكسل: البلاطات تحت بعضها، ولا صف بيتمرر جنبًا.",
                en: "Below 1024px: stack the tiles, or scroll them sideways in one row.",
            },
            options: [
                { value: "stack", label: { ar: "تحت بعضها", en: "Stacked" } },
                { value: "scroll", label: { ar: "صف بيتمرر", en: "Side scroller" } },
            ],
            default: "stack",
            showIf: { variant: ["bento"] },
        },
        {
            id: "headingLevel",
            type: "headingLevel",
            label: { ar: "مستوى العنوان", en: "Heading level" },
            default: 2,
        },
        ...colorFields(),
    ],
};
//# sourceMappingURL=collections.js.map